import { useState, useEffect } from 'react';
import { NavLink, useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard, CreditCard, ArrowLeftRight, Link2, Users, Plug, KeyRound,
  Webhook, Building2, ShieldCheck, ChevronsLeft, ChevronsRight, Menu, LogOut,
  Sun, Moon, ChevronDown, Wallet, Activity, Store, SlidersHorizontal, Rocket,
} from 'lucide-react';
import { useAuth } from '../lib/auth.jsx';
import { can } from '../lib/roles.js';
import { useTheme } from '../lib/theme.jsx';
import { Logo, LogoMark } from './Logo.jsx';
import { OnboardingChecklist } from './OnboardingChecklist.jsx';
import { TopBar } from './TopBar.jsx';

/**
 * Collapsible left sidebar (256px / 72px) with a sticky in-content top bar.
 * DB-1: below 1024px it becomes a drawer. Every screen is usable on a phone.
 */
/**
 * There are two different people who sign in here, and they need two different
 * sidebars.
 *
 * A merchant owner manages one business: their payments, their providers, their
 * keys. A platform admin has no business at all — their user record carries a
 * null merchantId — and every item in the merchant nav would either be empty or
 * throw for them. Showing them a greyed-out version of somebody else's product
 * is worse than showing them their own.
 */
const NAV = [
  {
    label: null,
    items: [
      { to: '/dashboard', label: 'Overview', icon: LayoutDashboard, exact: true },
      { to: '/payments', label: 'Payments', icon: CreditCard },
      { to: '/transactions', label: 'Transactions', icon: ArrowLeftRight },
      { to: '/payment-links', label: 'Payment links', icon: Link2, needs: 'MANAGE_PAYMENT_LINKS' },
      { to: '/customers', label: 'Customers', icon: Users },
    ],
  },
  {
    label: 'Get paid',
    items: [
      { to: '/pos', label: 'Point of sale', icon: Store, needs: 'TAKE_PAYMENT' },
      { to: '/activity', label: 'Activity', icon: Activity, needs: 'VIEW_ACTIVITY' },
      { to: '/providers', label: 'Payment providers', icon: Plug, needs: 'MANAGE_PROVIDERS' },
      { to: '/payment-methods', label: 'Payment methods', icon: Wallet, needs: 'MANAGE_METHODS' },
      { to: '/settings/custom-fields', label: 'Sale details', icon: SlidersHorizontal, needs: 'MANAGE_METHODS' },
    ],
  },
  {
    label: 'Developers',
    items: [
      { to: '/developers/api-keys', label: 'API keys', icon: KeyRound, needs: 'MANAGE_API_KEYS' },
      { to: '/developers/webhooks', label: 'Webhooks', icon: Webhook, needs: 'MANAGE_WEBHOOKS' },
    ],
  },
  {
    label: 'Account',
    items: [
      /**
       * Kept in the nav after setup is finished, not only while it is not.
       * The page is a standing answer to "what else could I turn on?" — the
       * optional half never stops being useful, and a link that vanishes the
       * moment you complete it is a link nobody finds again.
       */
      { to: '/setup', label: 'Setup', icon: Rocket },
      { to: '/settings/profile', label: 'Business profile', icon: Building2, needs: 'MANAGE_PROFILE' },
      { to: '/settings/team', label: 'Team', icon: Users, needs: 'MANAGE_TEAM' },
      { to: '/settings/security', label: 'Security', icon: ShieldCheck },
    ],
  },
];

/** §15: the platform operator's nav. Nothing here belongs to any one merchant. */
const ADMIN_NAV = [
  {
    label: null,
    items: [
      { to: '/admin', label: 'Overview', icon: LayoutDashboard, exact: true },
      { to: '/admin/merchants', label: 'Organisations', icon: Building2 },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/admin/payments', label: 'Payments', icon: CreditCard },
      { to: '/admin/health', label: 'System health', icon: Activity },
    ],
  },
  {
    label: 'Account',
    items: [{ to: '/settings/security', label: 'Security', icon: ShieldCheck }],
  },
];

function NavItem({ item, open }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.exact}
      title={!open ? item.label : undefined}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 rounded-lg px-3 py-2 transition-colors ${
          isActive ? 'bg-surface-2 font-medium text-ink' : 'text-ink-soft hover:bg-surface-2 hover:text-ink'
        }`
      }
    >
      {({ isActive }) => (
        <>
          {/* The signature: a 2px ink rule bleeding off the left edge. */}
          {isActive && (
            <span
              aria-hidden="true"
              className="absolute -left-2.5 top-1/2 h-5 w-[2px] -translate-y-1/2 rounded-full"
              style={{ background: 'rgb(var(--ink))' }}
            />
          )}
          <Icon
            className={`h-4 w-4 shrink-0 transition-colors ${
              isActive ? 'text-ink' : 'text-faint group-hover:text-ink-soft'
            }`}
          />
          {open && <span className="flex-1 truncate text-sm">{item.label}</span>}
        </>
      )}
    </NavLink>
  );
}

function NavGroup({ group, open }) {
  const [expanded, setExpanded] = useState(true);

  if (!group.label) {
    return <div className="space-y-0.5">{group.items.map((i) => <NavItem key={i.to} item={i} open={open} />)}</div>;
  }

  // Collapsed to 72px, group headers degrade to a thin rule.
  if (!open) {
    return (
      <>
        <div className="mx-2 my-2 h-px" style={{ background: 'rgb(var(--line))' }} />
        <div className="space-y-0.5">{group.items.map((i) => <NavItem key={i.to} item={i} open={open} />)}</div>
      </>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="group/hdr flex w-full items-center justify-between rounded px-3 pb-1.5 pt-1"
      >
        <span className="eyebrow transition-colors group-hover/hdr:text-muted">{group.label}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 text-faint transition-transform duration-150 ${expanded ? '' : '-rotate-90'}`}
        />
      </button>
      {expanded && (
        <div className="space-y-0.5">{group.items.map((i) => <NavItem key={i.to} item={i} open={open} />)}</div>
      )}
    </div>
  );
}

function SidebarContents({ open, onToggle, variant }) {
  const { user, merchant, signOut, isPlatformAdmin } = useAuth();
  const isDesktop = variant === 'desktop';
  const nav = isPlatformAdmin ? ADMIN_NAV : NAV;
  const home = isPlatformAdmin ? '/admin' : '/dashboard';

  return (
    <div className="flex h-full flex-col">
      <div
        className="flex h-[57px] items-center justify-between gap-2 border-b px-4"
        style={{ borderColor: 'rgb(var(--line))' }}
      >
        <Link to={home} className="min-w-0">
          {open ? <Logo /> : <LogoMark size={28} />}
        </Link>
        {isDesktop && (
          <button type="button" onClick={onToggle} className="btn-icon" aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}>
            {open ? <ChevronsLeft className="h-4 w-4" /> : <ChevronsRight className="h-4 w-4" />}
          </button>
        )}
      </div>

      {/*
        * Who you are operating as. For an admin that is the platform itself,
        * which is worth saying out loud — every number they are about to read
        * belongs to somebody else.
        */}
      {open && isPlatformAdmin && (
        <div
          className="mx-3 my-3 rounded-lg border px-3 py-2"
          style={{ borderColor: 'rgb(var(--accent-200, var(--line)))', background: 'rgb(var(--surface-2))' }}
        >
          <p className="truncate text-sm font-medium text-ink">Platform</p>
          <p className="mt-0.5 text-[11px] text-muted">Every organisation</p>
        </div>
      )}

      {/* Workspace identity — which business, and what state it is in. */}
      {open && !isPlatformAdmin && merchant && (
        <div className="mx-3 my-3 rounded-lg border px-3 py-2" style={{ borderColor: 'rgb(var(--line))' }}>
          <p className="truncate text-sm font-medium text-ink">{merchant.name}</p>
          <div className="mt-1 flex items-center gap-1.5">
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                merchant.status === 'active' ? 'bg-emerald-500'
                  : merchant.status === 'suspended' ? 'bg-red-500' : 'bg-amber-500'
              }`}
              aria-hidden="true"
            />
            <span className="text-[11px] capitalize text-muted">{merchant.status}</span>
          </div>
        </div>
      )}

      <nav className="flex-1 space-y-3 overflow-y-auto px-2.5 py-2" aria-label="Main">
        {/*
          * Rendering only. Every one of these is enforced again in middleware,
          * because a nav item that is merely hidden is still reachable by URL.
          * Hiding it keeps a cashier from being offered a screen that would
          * refuse them, which is politeness, not security.
          */}
        {nav
          .map((group) => ({
            ...group,
            items: group.items.filter((i) => !i.needs || can(user?.role, i.needs)),
          }))
          .filter((group) => group.items.length > 0)
          .map((group, i) => <NavGroup key={group.label ?? `g${i}`} group={group} open={open} />)}

        {/* MA-6: the checklist persists in the sidebar until complete. An admin
            has no onboarding of their own to finish. */}
        {open && !isPlatformAdmin && <div className="px-0.5 pt-1"><OnboardingChecklist compact /></div>}
      </nav>

      <div className="border-t p-3" style={{ borderColor: 'rgb(var(--line))' }}>
        {open ? (
          <>
            <div className="mb-2 min-w-0 px-1">
              <p className="truncate text-sm font-medium text-ink">{user?.name}</p>
              <p className="truncate text-xs text-muted">{user?.email}</p>
            </div>
            <button
              type="button"
              onClick={() => signOut()}
              className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-sm text-ink-soft transition-colors hover:bg-surface-2 hover:text-ink"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </>
        ) : (
          <button type="button" onClick={() => signOut()} className="btn-icon mx-auto" aria-label="Sign out">
            <LogOut className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function ThemeToggle() {
  const { isDark, toggle } = useTheme();
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      className="grid h-[34px] w-[34px] place-items-center rounded-md border text-ink-soft transition-colors hover:bg-surface-2 hover:text-ink"
      style={{ borderColor: 'rgb(var(--line))', background: 'rgb(var(--surface))' }}
    >
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

export function AppShell({ children }) {
  const [open, setOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // A drawer left open across a navigation hides the page just chosen.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <div className="relative flex min-h-screen bg-surface-2 text-ink">
      <aside
        className={`fixed inset-y-0 left-0 z-20 hidden flex-col border-r transition-[width] duration-200 lg:flex ${
          open ? 'w-[256px]' : 'w-[72px]'
        }`}
        style={{ background: 'rgb(var(--surface))', borderColor: 'rgb(var(--line))' }}
      >
        <SidebarContents open={open} onToggle={() => setOpen((v) => !v)} variant="desktop" />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 backdrop-blur-sm"
            style={{ background: 'rgb(0 0 0 / 0.45)' }}
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <aside
            className="absolute inset-y-0 left-0 flex w-[280px] max-w-[85vw] animate-slide-in-left flex-col border-r"
            style={{
              background: 'rgb(var(--surface))',
              borderColor: 'rgb(var(--line))',
              boxShadow: 'var(--shadow-lg)',
            }}
          >
            <SidebarContents open onToggle={() => {}} variant="mobile" />
          </aside>
        </div>
      )}

      <main
        className={`min-w-0 flex-1 overflow-x-hidden transition-[padding] duration-200 ${
          open ? 'lg:pl-[256px]' : 'lg:pl-[72px]'
        }`}
      >
        <div
          className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b px-4 py-3 lg:hidden"
          style={{ background: 'rgb(var(--surface))', borderColor: 'rgb(var(--line))' }}
        >
          <button type="button" onClick={() => setMobileOpen(true)} className="btn-icon -ml-1" aria-label="Open navigation">
            <Menu className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1"><Logo size={24} /></div>
          <ThemeToggle />
        </div>

        <div className="mx-auto max-w-content px-4 pb-20 pt-5 sm:px-6 lg:px-8">
          <TopBar />
          {children}
        </div>
      </main>

    </div>
  );
}

export default AppShell;
